import { useState, useEffect } from 'react';
import { createDirectLine } from 'botframework-webchat';
import type { DirectLineJSBotConnection } from 'botframework-webchat-core';
import {
    clearRehydrationData,
    getConversationIdFromToken,
    getCheckpointWatermark,
    getSessionToken,
    getUrlParam,
    isGenesysResume,
    isTokenFromGet,
    setSession,
    getTokenFromGet,
    requestChatBotEncrypted,
    sessionStorageAvailable,
} from '../helpers';
import { Session } from '../types';
import { useLocation } from './useLocation';
import { logEvent, setConversationId } from '../logging';
import { useConfig } from './useConfig';
import { Tier } from '../config';
import { GraceFeatureFlags } from '../featureFlags';
import { AmpFlagSet } from '../amplitude';
import isObjectEmpty from '../utils/isObjectEmpty';
import { initConversationStorage } from './useConversationStorage';

let sessionFromCookie = true;

export interface DirectLineSession {
    client: DirectLineJSBotConnection;
    session: Session;
}

export const shouldReconnect = (
    featureFlags: AmpFlagSet,
    session: Session
): boolean => {
    const shouldAttemptReconnect =
        featureFlags[GraceFeatureFlags.DirectLineReconnect] &&
        sessionFromCookie;
    if (isTokenFromGet(session)) {
        // if 'grace-tfg' cookie does not exist, we haven't initialized the conversation
        const tokenFromGet = getTokenFromGet(featureFlags);
        return <boolean>shouldAttemptReconnect && tokenFromGet !== null;
    }
    return <boolean>shouldAttemptReconnect;
};

export const checkUrlParams = (tier: Tier) => {
    if (getUrlParam('entrypoint')) {
        clearRehydrationData(tier);
    } else if (getUrlParam('tr') === 'logout') {
        clearRehydrationData(tier);
    }
};

export const useDirectLineSession = (
    shouldInitDirectLine: boolean,
    featureFlags: AmpFlagSet,
    healthSystemToken: string | null
): [DirectLineSession | undefined, boolean] => {
    const [directLine, setDirectLine] = useState<DirectLineSession>();
    const location = useLocation();
    const { authServiceEndpoint, tier, brandParentUrl } = useConfig();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!shouldInitDirectLine) {
            logEvent({
                name: 'Skipping DirectLine session initialization for now',
            });
            return undefined;
        }
        (async function (): Promise<void> {
            if (isObjectEmpty(featureFlags)) {
                return;
            }

            checkUrlParams(tier);

            let sessionToken = undefined;
            if (
                isGenesysResume() &&
                sessionStorageAvailable() &&
                sessionStorage.getItem('sessionToken')
            ) {
                sessionToken =
                    sessionStorage.getItem('sessionToken') || undefined;
                sessionStorage.removeItem('sessionToken');
            } else if (featureFlags[GraceFeatureFlags.DirectLineReconnect]) {
                sessionToken = getSessionToken(featureFlags) || undefined;
            }
            logEvent({ name: 'Attempting to setup DirectLine session' });
            setIsLoading(true);

            const session: Session = await requestChatBotEncrypted(
                featureFlags,
                { tier, authServiceEndpoint, brandParentUrl },
                healthSystemToken,
                sessionToken,
                location
            ); // pass location, if present
            sessionFromCookie = !session.isNewConversation;
            setSession(session);

            const conversationId = getConversationIdFromToken(
                session.connectorToken
            );
            if (conversationId) {
                setConversationId(conversationId);
                initConversationStorage(conversationId);
            }
            const watermark = getCheckpointWatermark(featureFlags);
            const shouldRehydrate = shouldReconnect(featureFlags, session);
            const dlOptions = {
                token: session.connectorToken,
                conversationId:
                    shouldRehydrate && conversationId
                        ? conversationId
                        : undefined,
                watermark: shouldRehydrate ? watermark : undefined,
            };

            let directLineClient: DirectLineJSBotConnection | undefined;
            try {
                directLineClient = createDirectLine(dlOptions);
            } catch (_e) {
                logEvent({ name: 'Failed to create DirectLine client' });
                setIsLoading(false);
                return;
            }
            if (directLineClient) {
                logEvent({ name: 'Successfully setup DirectLine session' });
            }
            setDirectLine({
                // This document may be helpful for understanding the createDirectLine method:
                // https://github.com/microsoft/BotFramework-WebChat/blob/master/docs/API.md
                client: directLineClient as DirectLineJSBotConnection,
                session,
            });
            setIsLoading(false);
            return;
        })();
    }, [shouldInitDirectLine, featureFlags, location]);

    return [directLine, isLoading];
};
